import type { ReactNode } from "react";
import { ArrowDownAZ, ArrowDownZA, ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { SortDir, SortState, SortValueType } from "@/lib/tableSort";

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

type SortableColumnHeaderProps = {
  label: ReactNode;
  sortKey?: string;
  sort?: SortState;
  onSort?: (key: string, direction: SortDir | null) => void;
  sortType?: SortValueType;
};

const DIRECTION_LABEL: Record<SortValueType, { asc: string; desc: string }> = {
  text: { asc: "A → Z", desc: "Z → A" },
  number: { asc: "Ascending", desc: "Descending" },
};

const ACTIVE_ARROW_CLASS = "text-red-500 dark:text-red-400";

export function SortableColumnHeader({ label, sortKey, sort, onSort, sortType = "number" }: SortableColumnHeaderProps) {
  if (!sortKey || !onSort) return <>{label}</>;
  const active = sort?.sortBy === sortKey;
  const direction = active ? sort?.sortDir : undefined;
  const directionLabel = DIRECTION_LABEL[sortType];
  const AscIcon = sortType === "text" ? ArrowDownAZ : ArrowUp;
  const DescIcon = sortType === "text" ? ArrowDownZA : ArrowDown;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Sort by ${typeof label === "string" ? label : "column"}`}
          className={cx(
            "-mx-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 outline-none transition-colors",
            "hover:bg-accent-foreground/15 focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          <span>{label}</span>
          {direction === "asc" ? (
            <AscIcon className={cx("h-4 w-4 shrink-0", ACTIVE_ARROW_CLASS)} aria-hidden="true" />
          ) : direction === "desc" ? (
            <DescIcon className={cx("h-4 w-4 shrink-0", ACTIVE_ARROW_CLASS)} aria-hidden="true" />
          ) : (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-90" aria-hidden="true" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[10rem] text-xs font-normal normal-case">
        <DropdownMenuItem
          onSelect={() => onSort(sortKey, "asc")}
          className={cx("gap-2", direction === "asc" && "font-semibold", direction === "asc" && ACTIVE_ARROW_CLASS)}
        >
          <AscIcon className={cx("h-4 w-4 shrink-0", direction === "asc" && ACTIVE_ARROW_CLASS)} aria-hidden="true" />
          {directionLabel.asc}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => onSort(sortKey, "desc")}
          className={cx("gap-2", direction === "desc" && "font-semibold", direction === "desc" && ACTIVE_ARROW_CLASS)}
        >
          <DescIcon className={cx("h-4 w-4 shrink-0", direction === "desc" && ACTIVE_ARROW_CLASS)} aria-hidden="true" />
          {directionLabel.desc}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSort(sortKey, null)} disabled={!active} className="gap-2">
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
          None
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function getAriaSort(
  sortKey: string | undefined,
  sort: SortState | undefined,
): "ascending" | "descending" | "none" | undefined {
  if (!sortKey) return undefined;
  if (sort?.sortBy !== sortKey) return "none";
  return sort.sortDir === "asc" ? "ascending" : "descending";
}
